<template>
  <div class="prodi-selector-card">

    <!-- Dekorasi pojok -->
    <div class="deco deco--tl" aria-hidden="true">
      <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="30" cy="30" r="50" fill="#4489C6" opacity="0.12"/>
        <circle cx="10" cy="10" r="20" fill="#6DD5FA" opacity="0.18"/>
      </svg>
    </div>
    <div class="deco deco--br" aria-hidden="true">
      <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="90" cy="90" r="55" fill="#F0D25B" opacity="0.15"/>
        <circle cx="110" cy="110" r="22" fill="#DF654D" opacity="0.12"/>
      </svg>
    </div>

    <div class="card-content">

      <!-- Tombol Kembali -->
      <button class="btn-back" @click="$emit('back')" aria-label="Kembali ke halaman sebelumnya">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/></svg>
        Kembali
      </button>

      <!-- Abby Header -->
      <div class="abby-header">
        <div class="abby-mascot">
          <img src="@/assets/pcu/2026/abby-smile-half.png" alt="Abby Mascot" class="abby-img"/>
        </div>
        <div class="abby-dialog">
          <h2 class="card-title">
            <span v-if="nickname">Hei, <span class="nickname-highlight">{{ nickname }}</span>! </span>Ada Jurusan Incaranmu? 🎓
          </h2>
          <p class="card-subtitle">
            Kalau kamu sudah punya bayangan jurusan yang kamu minati, pilih di sini!
            Kamu bisa memilih hingga <strong>3 jurusan</strong>.
          </p>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="loading-state">
        <div class="mini-spinner"></div>
        <p>Memuat daftar jurusan...</p>
      </div>

      <!-- Error -->
      <div v-else-if="loadError" class="error-state">
        <span class="error-icon">⚠️</span>
        <p>{{ loadError }}</p>
        <button class="retry-btn" @click="$emit('skip')">Lewati saja</button>
      </div>

      <!-- Prodi Grid -->
      <template v-else>
        <!-- Counter -->
        <div class="selection-counter" :class="{ 'has-selection': selectedIds.length > 0 }">
          <div class="counter-dots">
            <span
              v-for="i in 3"
              :key="i"
              class="counter-dot"
              :class="{ filled: i <= selectedIds.length }"
            ></span>
          </div>
          <span class="counter-label">
            <span v-if="selectedIds.length === 0">Belum ada yang dipilih</span>
            <span v-else-if="selectedIds.length === 3">3/3 — Sudah penuh! 🎉</span>
            <span v-else>{{ selectedIds.length }}/3 jurusan dipilih</span>
          </span>
        </div>

        <div class="prodi-grid">
          <button
            v-for="(name, id) in prodis"
            :key="id"
            class="prodi-chip"
            :class="{
              selected: selectedIds.includes(String(id)),
              'chip-disabled': selectedIds.length >= 3 && !selectedIds.includes(String(id))
            }"
            @click="toggleProdi(String(id))"
            :aria-pressed="selectedIds.includes(String(id))"
          >
            <span class="chip-check">
              <svg v-if="selectedIds.includes(String(id))" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M173.9 439.4l-166.4-166.4c-10-10-10-26.2 0-36.2l36.2-36.2c10-10 26.2-10 36.2 0L192 312.69l271.1-271.1c10-10 26.2-10 36.2 0l36.2 36.2c10 10 10 26.2 0 36.2l-307.3 307.3c-10 10-26.2 10-36.3 0z"/></svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z"/></svg>
            </span>
            <span class="chip-name">{{ name }}</span>
          </button>
        </div>

        <!-- Warning saat max tercapai -->
        <p class="max-warning" v-if="selectedIds.length >= 3">
          ✅ Kamu sudah memilih 3 jurusan. Batalkan pilihan untuk mengganti.
        </p>

        <!-- Hint lewati -->
        <p class="skip-hint" v-if="selectedIds.length < 1">Tidak tahu? Tenang, kamu bisa <em>lewati</em> langkah ini. 😊</p>

        <!-- Tombol Aksi -->
        <div class="action-buttons">
          <button class="btn-skip" @click="$emit('skip')">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"/></svg>
            Lewati
          </button>
          <button
            class="btn-next"
            :class="{ 'btn-active': selectedIds.length > 0 }"
            :disabled="selectedIds.length === 0"
            @click="handleNext"
          >
            Lanjutkan
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"/></svg>
          </button>
        </div>

        <p class="note-text">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-144c-17.7 0-32-14.3-32-32s14.3-32 32-32s32 14.3 32 32s-14.3 32-32 32z"/></svg>
          Pilihan jurusan ini bersifat opsional dan akan disimpan bersama hasil akhir kamu.
        </p>
      </template>

    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const props = defineProps({
  prodis: {
    type: Object,
    required: true,
    default: () => ({})
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  loadError: {
    type: String,
    default: null
  },
  nickname: {
    type: String,
    default: ''
  },
  initialSelectedIds: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['next', 'skip', 'back']);

const selectedIds = ref([...props.initialSelectedIds]);

function toggleProdi(id) {
  const idx = selectedIds.value.indexOf(id);
  if (idx > -1) {
    selectedIds.value.splice(idx, 1);
  } else if (selectedIds.value.length < 3) {
    selectedIds.value.push(id);
  }
}

function handleNext() {
  if (selectedIds.value.length === 0) return;
  emit('next', [...selectedIds.value]);
}
</script>

<style scoped>
.prodi-selector-card {
  background: #ffffff;
  border-radius: 28px;
  padding: 2.5rem;
  width: 100%;
  max-width: 620px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.12);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.7);
  animation: card-in 0.5s cubic-bezier(0.25, 0.8, 0.25, 1) both;
}

@keyframes card-in {
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)    scale(1); }
}

/* Dekorasi sudut */
.deco {
  position: absolute;
  width: 160px;
  height: 160px;
  pointer-events: none;
  z-index: 0;
}
.deco--tl { top: -40px; left: -40px; }
.deco--br { bottom: -40px; right: -40px; }

.card-content {
  position: relative;
  z-index: 1;
}

/* --- Abby Header --- */
.abby-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1.5rem;
}

.abby-mascot {
  flex-shrink: 0;
}

.abby-img {
  width: 130px;
  object-fit: contain;
  display: block;
  animation: float-abby 3s ease-in-out infinite;
  -webkit-mask-image: linear-gradient(to bottom, black 88%, transparent 100%);
  mask-image: linear-gradient(to bottom, black 88%, transparent 100%);
}

@keyframes float-abby {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-8px); }
}

.abby-dialog {
  position: relative;
  background: #ffffff;
  border: 2px solid #26488A;
  border-radius: 16px;
  padding: 14px 16px;
  width: 100%;
  box-shadow: 4px 4px 0 #26488A;
  display: flex;
  flex-direction: column;
  gap: 4px;
  animation: pop-in 0.4s ease;
}

.abby-dialog::before {
  content: "";
  position: absolute;
  top: -12px;
  left: 50px;
  width: 0;
  height: 0;
  border-left: 12px solid transparent;
  border-right: 12px solid transparent;
  border-bottom: 12px solid #26488A;
}

.abby-dialog::after {
  content: "";
  position: absolute;
  top: -10px;
  left: 52px;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid white;
}

@keyframes pop-in {
  0%   { transform: scale(0.95); opacity: 0; }
  100% { transform: scale(1);    opacity: 1; }
}

.card-title {
  color: #26488A;
  font-size: 1.4rem;
  font-weight: 800;
  margin: 0 0 0.35rem 0;
  text-align: left;
}

.nickname-highlight {
  color: #DF654D;
}

.card-subtitle {
  color: #555;
  font-size: 0.9rem;
  line-height: 1.55;
  margin: 0;
  text-align: left;
}

.card-subtitle strong { color: #26488A; }
.card-subtitle em     { color: #2980B9; font-style: normal; font-weight: 600; }

/* --- Loading / Error --- */
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem 0;
  color: #555;
}

.mini-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(68, 137, 198, 0.25);
  border-top-color: #4489C6;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.error-icon { font-size: 2rem; }
.retry-btn {
  padding: 0.5rem 1.5rem;
  background: #4489C6;
  color: #fff;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  font-weight: 600;
  font-family: 'Epilogue', sans-serif;
  transition: background 0.2s;
}
.retry-btn:hover { background: #26488A; }

/* --- Counter --- */
.selection-counter {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: center;
  margin-bottom: 1.25rem;
  padding: 0.6rem 1.25rem;
  background: #f4f8fc;
  border-radius: 50px;
  border: 1.5px solid #dce9f5;
  transition: all 0.3s ease;
}

.selection-counter.has-selection {
  background: #e8f3fc;
  border-color: #4489C6;
}

.counter-dots {
  display: flex;
  gap: 6px;
}

.counter-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #c5d8ed;
  transition: background 0.3s ease, transform 0.2s ease;
}

.counter-dot.filled {
  background: #4489C6;
  transform: scale(1.2);
}

.counter-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #26488A;
}

/* --- Prodi Grid --- */
.prodi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(155px, 1fr));
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.prodi-chip {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.75rem;
  background: #f8f9fa;
  border: 1.5px solid #e0e8f0;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  font-family: 'Epilogue', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  color: #34495e;
  transition: all 0.2s ease;
  line-height: 1.35;
}

.prodi-chip:hover:not(.chip-disabled) {
  border-color: #4489C6;
  background: #edf4fb;
  box-shadow: 0 3px 8px rgba(68, 137, 198, 0.12);
}

.prodi-chip.selected {
  border-color: #26488A;
  background: linear-gradient(135deg, #26488A, #4489C6);
  color: #fff;
  font-weight: 600;
  box-shadow: 0 3px 12px rgba(38, 72, 138, 0.25);
}

.prodi-chip.chip-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.chip-check {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1.5px solid currentColor;
  transition: all 0.2s;
}

.prodi-chip.selected .chip-check {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.8);
}

.chip-check svg {
  width: 8px;
  height: 8px;
  fill: currentColor;
}

.chip-name { flex: 1; }

/* --- Warning --- */
.max-warning {
  font-size: 0.8rem;
  color: #2980B9;
  text-align: center;
  margin-bottom: 0.75rem;
  font-weight: 500;
  animation: fade-in 0.3s ease;
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* --- Tombol Aksi --- */
.action-buttons {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  justify-content: center;
}

.btn-skip,
.btn-next {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.8rem 1.75rem;
  border-radius: 50px;
  font-family: 'Epilogue', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-skip svg,
.btn-next svg {
  width: 12px;
  height: 12px;
  fill: currentColor;
}

.btn-skip {
  background: transparent;
  border: 2px solid #bdc3c7;
  color: #7f8c8d;
}

.btn-skip:hover {
  border-color: #95a5a6;
  color: #555;
  transform: translateY(-2px);
}

.btn-next {
  background: #bdc3c7;
  border: 2px solid #bdc3c7;
  color: #fff;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-next.btn-active {
  background: linear-gradient(135deg, #26488A, #4489C6);
  border-color: transparent;
  cursor: pointer;
  opacity: 1;
  box-shadow: 0 5px 18px rgba(38, 72, 138, 0.3);
}

.btn-next.btn-active:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(38, 72, 138, 0.4);
}

/* --- Tombol Kembali --- */
.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.85rem 0.35rem 0.6rem;
  background: transparent;
  border: 1.5px solid #dce9f5;
  border-radius: 50px;
  color: #4489C6;
  font-family: 'Epilogue', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 1rem;
}

.btn-back svg {
  width: 9px;
  height: 9px;
  fill: currentColor;
  flex-shrink: 0;
}

.btn-back:hover {
  background: #edf4fb;
  border-color: #4489C6;
  color: #26488A;
  transform: translateX(-2px);
}

/* --- Skip hint --- */
.skip-hint {
  font-size: 0.85rem;
  color: #7f8c8d;
  text-align: center;
  margin-bottom: 0.5rem;
}

.skip-hint em {
  color: #2980B9;
  font-style: normal;
  font-weight: 600;
}

/* --- Catatan kecil --- */
.note-text {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  justify-content: center;
  margin-top: 1.25rem;
  font-size: 0.75rem;
  color: #95a5a6;
  text-align: center;
  line-height: 1.5;
}

.note-text svg {
  width: 12px;
  height: 12px;
  fill: currentColor;
  flex-shrink: 0;
  margin-top: 2px;
}

/* --- Responsive --- */
@media (max-width: 480px) {
  .prodi-selector-card {
    padding: 1.75rem 1.25rem;
    border-radius: 20px;
  }

  .card-title {
    font-size: 1.4rem;
  }

  .prodi-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .action-buttons {
    flex-direction: column-reverse;
  }

  .btn-skip,
  .btn-next {
    justify-content: center;
  }
}
</style>
